import React, { useState } from 'react';
import { Modal, Button, Steps } from 'antd';

import StepSelect from './StepSelect';
import StepReview from './StepReview';
import StepActions from './StepActions';
import StepCreating from './StepCreating';
import StepGenerateNewStatement from './StepGenerateNewStatement';
import useStatementPatchActions from './hooks/useStatementPatchActions';
import useStatementPatchForm from './hooks/useStatementPatchForm';
import useStatementGenerate from './hooks/useStatementGenerate';

export default function PatchStatementsModal({
  statements,
  accountType,
  accountId,
  productId,
  userId,
  userName,
  refresh
}) {
  
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    furnishingStatements,
    statementsForm,
    patchSubmit,
    statementsToBePatched,
    newStatements,
    patchStatus,
    patchStatement,
    statementsFormReset
  } = useStatementPatchForm({
    statements
  });
  
  const {
    reportingCycles,
    newStatementsForm,
    newFurnishingStatements,
    generateStatements
  } = useStatementGenerate({ newStatements });

  const {
    steps,
    currentStep,
    next,
    back,
    cancel,
    isNextDisabled,
    isBackDisabled,
    isLoading,
    triggerActionsRender
  } = useStatementPatchActions({
    statementsForm,
    newStatementsForm,
    
    newStatements,
    statementsToBePatched,

    patchStatus,
    reportingCycles,

    statementsFormReset
  });

  const showModal = () => {
    setIsModalOpen(true);
  };

  const cancelPatch = () => {
    // Patches save at the review step and statements at the create step, so closing
    // early can still leave saved changes that the statement list needs to show
    const hasSavedChanges = Object.values(patchStatus).some((status) => status.status)
      || Object.values(reportingCycles).some((cycle) => cycle.status);

    cancel();
    setIsModalOpen(false);
    if (hasSavedChanges) refresh();
  };

  const nextAction = () => {
    if (currentStep === 3) {
      refresh();
      cancel();
      setIsModalOpen(false);
    } else {
      next();
    }
  }

  return (
    <>
      <Button danger onClick={showModal} block>
        Patch Statements
      </Button>
      { isModalOpen && <Modal
        title={`Patch ${accountType} Account - ${userName}`}
        open={isModalOpen}
        width="1600px"
        footer={null}
        onCancel={cancelPatch}>
        <div style={{ height: '500px', maxHeight: '500px', overflow: 'hidden auto'}}>
          { /* // first step is to select the statements to patch */ }
          { currentStep === 0 && <StepSelect
              furnishingStatements={furnishingStatements}
              form={statementsForm}
              triggerActionsRender={triggerActionsRender}
              submit={patchSubmit}
            /> }

          { /* // Second step is to review the statements to patch */ }
          { currentStep === 1 && <StepReview
              statements={statementsToBePatched}
              patchStatus={patchStatus}
              patchStatement={patchStatement}
            /> }

          { /* // Third step is to patch the statements */ }
          { currentStep === 2 && <StepCreating
              accountId={accountId}
              productId={productId}
              userId={userId}
              reportingCycles={reportingCycles}
              form={newStatementsForm}
              newFurnishingStatements={newFurnishingStatements}
              triggerActionsRender={triggerActionsRender}
              generateStatements={generateStatements}
            /> }

          { /* // Second step is to review the statements to patch */ }
          { currentStep === 3 && <StepGenerateNewStatement
              statements={statementsForm.getFieldValue("statements")}
              patchStatus={patchStatus}
              reportingCycles={reportingCycles}
            /> }
          
        </div>
        { /* // Actions Buttons */ }
        <div>&nbsp;</div>
        <StepActions
          isLoading={isLoading}
          isNextDisabled={isNextDisabled}
          isBackDisabled={isBackDisabled}
          next={nextAction}
          back={back}
          cancelPatch={cancelPatch}
          currentStep={currentStep}
          />
        <div>&nbsp;</div>
        <Steps items={steps} current={currentStep} />
        <div>&nbsp;</div>
      </Modal>}
    </>
  );
}